/**
 * Admin Payments — view all consultation payments with doctor info,
 * advanced filters, aging/overdue indicators, settlement references,
 * and batch-settle capability so the admin can settle doctor fees on time.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

/* ── helpers ─────────────────────────────────────────────── */

const statusBadge = (s) => {
  switch (s) {
    case 'completed':  return 'bg-green-100 text-green-700';
    case 'settled':    return 'bg-blue-100 text-blue-700';
    case 'pending':    return 'bg-yellow-100 text-yellow-700';
    case 'failed':     return 'bg-red-100 text-red-700';
    default:           return 'bg-gray-100 text-gray-600';
  }
};

const agingLabel = (days) => {
  if (days >= 7) return { text: `${days}d overdue — urgent`, cls: 'text-red-600 font-bold' };
  if (days >= 3) return { text: `${days}d unsettled — overdue`, cls: 'text-red-500 font-semibold' };
  if (days >= 1) return { text: `${days}d unsettled`, cls: 'text-amber-600' };
  return { text: 'Today', cls: 'text-gray-500' };
};

const toISODate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const presetRange = (key) => {
  const now = new Date();
  const today = toISODate(now);
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'week': {
      const w = new Date(now);
      w.setDate(w.getDate() - 7);
      return { from: toISODate(w), to: today };
    }
    case 'month': {
      const m = new Date(now);
      m.setDate(m.getDate() - 30);
      return { from: toISODate(m), to: today };
    }
    default:
      return { from: '', to: '' };
  }
};

/* ── component ───────────────────────────────────────────── */

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [doctorSearch, setDoctorSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');

  // Settle state
  const [settlingId, setSettlingId] = useState(null);
  const [batchSettling, setBatchSettling] = useState(false);
  const [settleRefMap, setSettleRefMap] = useState({});    // per-payment ref inputs
  const [batchRef, setBatchRef] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  /* ---- data fetching ---- */
  const load = useCallback(async () => {
    try {
      const params = { status: statusFilter, sort: sortOrder };
      if (doctorSearch.trim()) params.doctor = doctorSearch.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to = dateTo;
      const res = await adminAPI.getPayments(params);
      setPayments(res.data || []);
      setSummary(res.summary || {});
    } catch (err) {
      toast.error(err?.message || 'Failed to load payments.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, doctorSearch, dateFrom, dateTo, sortOrder]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  /* ---- single settle ---- */
  const handleSettle = async (paymentId) => {
    const ref = (settleRefMap[paymentId] || '').trim();
    if (!window.confirm(`Mark this payment as settled with the doctor?${ref ? ` Reference: ${ref}` : ''}`)) return;
    setSettlingId(paymentId);
    try {
      await adminAPI.settlePayment({ payment_id: paymentId, settlement_reference: ref });
      toast.success('Payment settled!');
      load();
    } catch (err) {
      toast.error(err?.message || 'Failed to settle payment.');
    } finally {
      setSettlingId(null);
    }
  };

  /* ---- batch settle ---- */
  const unsettledIds = useMemo(
    () => payments.filter((p) => p.status === 'completed').map((p) => p.id),
    [payments],
  );

  const handleBatchSettle = async () => {
    if (unsettledIds.length === 0) return;
    if (!window.confirm(`Settle all ${unsettledIds.length} unsettled payment(s)?`)) return;
    setBatchSettling(true);
    try {
      const res = await adminAPI.settlePayment({ payment_ids: unsettledIds, settlement_reference: batchRef.trim() });
      toast.success(res.message || 'Batch settled!');
      setBatchRef('');
      load();
    } catch (err) {
      toast.error(err?.message || 'Batch settle failed.');
    } finally {
      setBatchSettling(false);
    }
  };

  /* ---- computed ---- */
  const overdueCount = summary.overdue_count || payments.filter((p) => p.is_overdue).length;
  const totalUnsettled = summary.total_unsettled || 0;
  const totalSettled = summary.total_settled || 0;
  const unsettledCount = summary.unsettled_count || 0;
  const overdueAmount = summary.overdue_amount || 0;

  /* ---- date preset handler ---- */
  const applyPreset = (key) => {
    const { from, to } = presetRange(key);
    setDateFrom(from);
    setDateTo(to);
  };

  return (
    <Layout title="Payments" role="admin" showBack>
      <div className="p-4 pb-24 space-y-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-900 rounded-2xl p-5 text-white">
          <p className="text-green-200 text-sm">Financial Overview</p>
          <h2 className="text-xl font-bold mt-1">Consultation Payments</h2>
          <p className="text-green-300 text-sm mt-1">Settle doctor fees after M-Pesa collection</p>
        </div>

        {/* ── Overdue alert banner ── */}
        {overdueCount > 0 && (
          <div className="bg-red-50 border border-red-300 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-red-800">
                {overdueCount} payment{overdueCount > 1 ? 's' : ''} overdue ({'>'}3 days unsettled)
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                KES {overdueAmount.toLocaleString()} needs immediate settlement to avoid inconsistencies.
              </p>
              <button
                onClick={() => setStatusFilter('overdue')}
                className="mt-2 text-xs font-semibold text-red-700 underline"
              >
                Show overdue only
              </button>
            </div>
          </div>
        )}

        {/* ── Summary cards ── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card text-center py-3">
            <p className="text-lg font-bold text-green-600">KES {totalUnsettled.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Unsettled ({unsettledCount})</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-lg font-bold text-blue-600">KES {totalSettled.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Settled</p>
          </div>
          {overdueCount > 0 && (
            <div className="card text-center py-3 border-red-200 bg-red-50 col-span-2">
              <p className="text-lg font-bold text-red-600">KES {overdueAmount.toLocaleString()}</p>
              <p className="text-xs text-red-500">{overdueCount} overdue ({'>'} 3 days)</p>
            </div>
          )}
        </div>

        {/* ── Status tabs ── */}
        <div className="flex gap-2 overflow-x-auto">
          {['all', 'completed', 'overdue', 'settled', 'pending', 'failed'].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`relative px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === f
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'overdue' ? 'Overdue' : f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'overdue' && overdueCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {overdueCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Filters toggle ── */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          {showFilters ? 'Hide filters' : 'More filters'}
        </button>

        {showFilters && (
          <div className="card space-y-3">
            {/* Doctor search */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Doctor name</label>
              <input
                type="text"
                value={doctorSearch}
                onChange={(e) => setDoctorSearch(e.target.value)}
                placeholder="Search doctor..."
                className="input-field mt-1"
              />
            </div>

            {/* Date presets */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date range</label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'Last 7 days' },
                  { key: 'month', label: 'Last 30 days' },
                  { key: 'clear', label: 'All time' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => key === 'clear' ? (setDateFrom(''), setDateTo('')) : applyPreset(key)}
                    className="px-3 py-1 text-xs rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-field text-sm flex-1" />
                <span className="self-center text-gray-400 text-xs">to</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-field text-sm flex-1" />
              </div>
            </div>

            {/* Sort */}
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sort</label>
              <button
                onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                className="flex items-center gap-1 text-sm text-primary-600 font-medium"
              >
                {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
                <svg className={`w-4 h-4 transition-transform ${sortOrder === 'oldest' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ── Batch settle bar (visible when viewing unsettled) ── */}
        {unsettledIds.length > 0 && ['all', 'completed', 'overdue'].includes(statusFilter) && (
          <div className="card bg-blue-50 border-blue-200 space-y-2">
            <p className="text-sm font-semibold text-blue-800">
              Batch Settle — {unsettledIds.length} payment{unsettledIds.length > 1 ? 's' : ''}
            </p>
            <input
              type="text"
              value={batchRef}
              onChange={(e) => setBatchRef(e.target.value)}
              placeholder="Settlement reference (optional)"
              className="input-field text-sm"
            />
            <button
              onClick={handleBatchSettle}
              disabled={batchSettling}
              className="w-full py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {batchSettling && <div className="spinner w-4 h-4 border-2" />}
              Settle All {unsettledIds.length} Payments
            </button>
          </div>
        )}

        {/* ── Payments list ── */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}
          </div>
        ) : payments.length === 0 ? (
          <div className="card text-center py-10 text-gray-400">
            <p className="text-4xl mb-2">💳</p>
            <p>No payments found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((pay) => {
              const aging = pay.status === 'completed' ? agingLabel(pay.days_unsettled || 0) : null;
              return (
                <div
                  key={pay.id}
                  className={`card space-y-2 ${pay.is_overdue ? 'border-red-300 bg-red-50/40' : ''}`}
                >
                  {/* Top row: amount + status + aging */}
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-gray-900">
                      KES {(pay.paid_amount || pay.amount || 0).toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      {aging && (
                        <span className={`text-[11px] ${aging.cls}`}>{aging.text}</span>
                      )}
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusBadge(pay.status)}`}>
                        {pay.status}
                      </span>
                    </div>
                  </div>

                  {/* Doctor info */}
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{pay.doctor_name}</p>
                      <p className="text-xs text-gray-500">{pay.doctor_phone}</p>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="text-xs text-gray-400 space-y-0.5">
                    {pay.mpesa_receipt_number && <p>Receipt: {pay.mpesa_receipt_number}</p>}
                    {pay.phone_number && <p>Patient phone: {pay.phone_number}</p>}
                    {pay.completed_at && (
                      <p>Paid: {new Date(pay.completed_at).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                    )}
                    {pay.doctor_settled_at && (
                      <p>Settled: {new Date(pay.doctor_settled_at).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                    )}
                    {pay.settlement_reference && <p>Settlement ref: {pay.settlement_reference}</p>}
                  </div>

                  {/* Settle section (completed / unsettled only) */}
                  {pay.status === 'completed' && (
                    <div className="space-y-1.5 pt-1 border-t border-gray-100">
                      <input
                        type="text"
                        value={settleRefMap[pay.id] || ''}
                        onChange={(e) => setSettleRefMap((prev) => ({ ...prev, [pay.id]: e.target.value }))}
                        placeholder="Settlement reference (optional)"
                        className="input-field text-sm"
                      />
                      <button
                        onClick={() => handleSettle(pay.id)}
                        disabled={settlingId === pay.id}
                        className="w-full py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                      >
                        {settlingId === pay.id && <div className="spinner w-4 h-4 border-2" />}
                        Mark as Settled with Doctor
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPayments;
